import {
  EditOutlined,
  CopyOutlined,
  DownloadOutlined,
  PrinterOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import ToolbarTable, {
  ToolbarButton,
} from "shared/components/toolbarTable/ToolbarTable";
import { MarketingMaterialTableItem } from "shared/types/marketingMaterials";
import { ROUTES, DisabledPrintDeliveryMessage } from "./utils/constants";
import { useDataListURLGlobalFilter } from "shared/components/dataListURL/useDataListURLGlobalFilter";
import { useLayoutPreference } from "shared/hooks/useLayoutPreference";
import { useDataListURLContext } from "shared/components/dataListURL/dataListURLContext";
import { useDataListURLSorter } from "shared/components/dataListURL/useDataListURLSorter";
import useNavigateWithSearch from "shared/hooks/useNavigateWithSearch";
import { useMarketingMaterialActions } from "./hooks/useMarketingMaterialActions";

type Props = {
  isPdfDownloading: boolean;
  selectedItems: MarketingMaterialTableItem[];
  getSendByEmailStatus: (selectedItems: MarketingMaterialTableItem[]) => {
    isSendByEmailDisabled: boolean;
    sendByEmailTooltip: string;
  };
  onSendByEmail: (selectedItems: MarketingMaterialTableItem[]) => void;
  getPdfDeliveryData: (selectedItems: MarketingMaterialTableItem[]) => void;
};

const localStorageLayoutKey = "MarketingMaterialsLayout";

export const Toolbar = ({
  isPdfDownloading,
  selectedItems,
  getSendByEmailStatus,
  getPdfDeliveryData,
  onSendByEmail,
}: Props) => {
  const [layout, setLayout] = useLayoutPreference(localStorageLayoutKey);
  const { globalFilter, setGlobalFilter } = useDataListURLGlobalFilter();
  const { setSelectedIds } = useDataListURLContext();
  const { sortKey, sortOrder, sortItems } = useDataListURLSorter<
    keyof MarketingMaterialTableItem,
    MarketingMaterialTableItem
  >();
  const { onDelete, onDuplicate } = useMarketingMaterialActions();
  const navigateWithSearch = useNavigateWithSearch();

  const { isSendByEmailDisabled, sendByEmailTooltip } = useMemo(
    () => getSendByEmailStatus(selectedItems),
    [selectedItems, getSendByEmailStatus],
  );

  const toolbarContents: ToolbarButton = {
    Edit: {
      disabled: selectedItems.length !== 1,
      icon: <EditOutlined />,
      onClick: () => {
        const [item] = selectedItems;
        navigateWithSearch(`${ROUTES.edit(item.id)}`);
      },
    },
    Duplicate: {
      disabled:
        selectedItems.length !== 1 || !!selectedItems[0]?.templateErrorStatus,
      icon: <CopyOutlined />,
      onClick: () => {
        const selectedMaterial = selectedItems[0];
        if (!selectedMaterial) return;
        onDuplicate({
          material: selectedMaterial,
          mode: "optimistic",
        });
      },
      extraInfo: {
        tooltip:
          selectedItems.length !== 1
            ? "Only one material can be duplicated at a time"
            : selectedItems[0]?.templateErrorStatus?.message,
      },
    },
    New: {
      onClick: () => {
        navigateWithSearch("new");
      },
      extraInfo: {
        text: "New Marketing Material",
        tooltip: "New Marketing Material",
      },
    },
    Delete: {
      disabled: false,
      icon: <EditOutlined />,
      onClick: () => {
        onDelete({
          ids: selectedItems.map(item => item.id),
          onSuccess: () => setSelectedIds([]),
          mode: "optimistic",
        });
      },
    },
    DownloadPDF: {
      disabled:
        isPdfDownloading ||
        !selectedItems.length ||
        selectedItems.some(
          item => !item.templateDeliveryMethods?.includes("download"),
        ) ||
        selectedItems.some(item => item.templateStatus !== "PUBLISHED"),
      onClick: () => {
        if (!selectedItems.length) return;
        getPdfDeliveryData(selectedItems);
      },
      extraInfo: {
        title: "Download",
        tooltipText: "Download",
        icon: <DownloadOutlined />,
      },
    },
    Print: {
      disabled: true,
      extraInfo: {
        title: "Order Prints",
        tooltipText: DisabledPrintDeliveryMessage,
        icon: <PrinterOutlined />,
      },
    },
    CustomButton: {
      disabled: isSendByEmailDisabled,
      onClick: () => {
        onSendByEmail(selectedItems);
      },
      extraInfo: {
        text: "Send by Email",
        title: "Send by Email",
        tooltipText: sendByEmailTooltip,
        icon: <MailOutlined />,
      },
    },
  };

  return (
    <ToolbarTable
      layout={layout}
      titleTooltip="Search"
      searchPlaceholder="Search"
      toolbarContents={toolbarContents}
      onSearch={setGlobalFilter}
      searchValue={globalFilter ?? undefined}
      disableSearch={false}
      onClickLayout={setLayout}
      previewFormat={false}
      sortingOrder={sortKey && sortOrder ? [sortKey, sortOrder] : undefined}
      onSortChange={([columnKey, order]) => {
        sortItems(columnKey, order);
      }}
      includeFilterOnTableLayout={true}
    />
  );
};
